import React, { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import openAI from "./assets/openai-2.svg";
import google from "./assets/google-g-2015.svg";
import anthropic from "./assets/anthropic-1.svg";
import twilio from "./assets/twilio.svg";
import Footer from "../../components/Footer/Footer";
import CalendarModal from "../../components/CalendarModal/CalendarModal";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Chip,
  Modal,
} from "@mui/material";
import { CheckCircle, Cancel, Warning, ExpandMore } from "@mui/icons-material";
import Navbar from "../../components/NavigationMenu/NavBar";
import beanbagLogo from "../Campaigns/assets/BeanbagLogo.svg";
import { color } from "framer-motion";
import { teal } from "@mui/material/colors";
import VapiLogo from "./assets/VapiLogo.jpeg";
import DeepGramLogo from "./assets/DeepGramLogo.jpeg";
import ElevenLabsLogo from "./assets/ElevenLabsLogo.png";
import vonageLogo from "./assets/vonageIcon.png";
import Anthropic from "./assets/anthropicLogo.png";
import playAi from "./assets/playAi.png";
import openmic from "./assets/openmic.jpeg";
import retellLogo from "./assets/retellLogo.png";

// Competitor data configuration
const competitorData = {
  vapi: {
    name: "Vapi",
    logo: VapiLogo,
    description:
      "Vapi is a developer-focused platform for creating voice agents. It offers flexibility by letting teams choose their own speech-to-text, text-to-speech, and language models, and is well-suited for engineering-driven projects where fine control and customization are priorities.",
    pricing: "$0.40/min",
    pricingBreakdown: [
      { price: "$0.05/min", label: "Hosting", color: "#4a5568" },
      { price: "$0.014/min", label: "Telephony ", color: "#718096" },
      { price: "$0.08/min", label: "LLM", color: "#a0aec0" },
      { price: "$0.24/min", label: "TTS", color: "#cbd5e0" },
      { price: "$0.017/min", label: "STT", color: "#e2e8f0" },
    ],
    pricingIcons: [
      [VapiLogo],
      [DeepGramLogo, ElevenLabsLogo, playAi],
      [google, openAI, Anthropic],
      [openAI, DeepGramLogo],
      [twilio, vonageLogo],
    ],
  },
  openmic: {
    name: "OpenMic",
    logo: openmic, // Replace with actual OpenMic logo when available
    description:
      "OpenMic is an open-source toolkit for building LLM-powered voice agents. It offers streaming APIs and simple integrations, making it easier for developers to create real-time conversational voice experiences for different use cases.",
    pricing: "$0.29/min",
    pricingBreakdown: [
      { price: "$0.29/min", label: "All Inclusive", color: "#4a5568" },
    ],
    pricingIcons: [
      [beanbagLogo], // Replace with OpenMic logo
    ],
  },

  retell: {
    name: "Retell AI",
    logo: retellLogo,
    description:
      "Retell AI is a voice AI platform that enables developers to design, deploy, and manage LLM-powered voice agents. It provides low-latency, streaming APIs for real-time, human-like conversations, making it suitable for customer support, sales, and service automation at scale.",
    pricing: "$0.20/min",
    pricingBreakdown: [
      { price: "Nil", label: "Hosting", color: "#4a5568" },
      { price: "$0.015/min", label: "Transport ", color: "#718096" },
      { price: "$0.06/min", label: "LLM", color: "#a0aec0" },
      { price: "$0.08/min", label: "TTS", color: "#cbd5e0" },
      { price: "$0.05/min", label: "STT", color: "#e2e8f0" },
    ],
    pricingIcons: [
      [retellLogo],
      [google, openAI, Anthropic],
      [ElevenLabsLogo, openAI],
      [DeepGramLogo, openAI],
    ],
  },
};

// Parse slug to get competitor key
const parseComparisonSlug = (slug) => {
  // Expected format: "beanbag-ai-vs-competitor"
  const parts = slug.split("-vs-");
  if (parts.length === 2) {
    return parts[1].replace(/-/g, "").toLowerCase();
  }
  return "vapi"; // Default fallback
};

const ComparisonPage = () => {
  const { comparisonSlug } = useParams();
  const [expandedFAQ, setExpandedFAQ] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // Get competitor data based on slug
  const competitorKey = parseComparisonSlug(
    comparisonSlug || "beanbag-ai-vs-vapi"
  );
  const competitor = competitorData[competitorKey] || competitorData.vapi;

  const handleFAQChange = (panel) => (event, isExpanded) => {
    setExpandedFAQ(isExpanded ? panel : false);
  };

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  // Dynamic features data based on competitor
  const getFeaturesData = (competitorKey) => {
    if (competitorKey === "openmic") {
      return [
        {
          feature: "LLM Model & Intelligence",
          beanbag:
            "Powered with native speech-to-speech reasoning, doesn't need separate STT/TTS layers",
          competitor: "Good TTS + STT + LLM",
          hasBeanbag: true,
          hasCompetitor: false,
        },
        {
          feature: "Custom LLM Support",
          beanbag: "Flexible - bring your own model or use the default option",
          competitor: "Offers flexibility via integrations",
          hasBeanbag: true,
          hasCompetitor: true,
        },
        {
          feature: "Voice & Audio Quality",
          beanbag: "Natural, expressive, and human-like voices",
          competitor: "Strong quality",
          hasBeanbag: true,
          hasCompetitor: true,
        },
        {
          feature: "Latency",
          beanbag: "Sub-300ms end-to-end latency",
          competitor: "500-800ms on many interactions",
          hasBeanbag: true,
          hasCompetitor: false,
        },
        {
          feature: "Interruptibility (Barge-In Handling)",
          beanbag:
            "Smooth mid-sentence interruption and recovery, built-in VAD events",
          competitor: "Supported",
          hasBeanbag: true,
          hasCompetitor: true,
        },
        {
          feature: "Multilingual Support",
          beanbag: "Supports 50+ languages",
          competitor: "Available, exact number not reported",
          hasBeanbag: true,
          hasCompetitor: true,
        },
        {
          feature: "Custom Telephony Integration",
          beanbag: "Native telephony + custom telephony integration",
          competitor: "Inbound/outbound support",
          hasBeanbag: true,
          hasCompetitor: true,
        },
        {
          feature: "Warm Transfer",
          beanbag: "Easy hand-off to a live human agent",
          competitor: "Human transfers supported",
          hasBeanbag: true,
          hasCompetitor: true,
        },
        {
          feature: "IVR & DTMF Support",
          beanbag:
            "Touch-tone recognition and menu navigation supported for compatibility",
          competitor: "Not Supported",
          hasBeanbag: true,
          hasCompetitor: false,
        },
        {
          feature: "Post-Call Analytics",
          beanbag: "Transcripts, summaries, and call analytics",
          competitor:
            "Real-time analytics, conversation insights, performance metrics",
          hasBeanbag: true,
          hasCompetitor: true,
        },
        {
          feature: "Ease of Use",
          beanbag: "No-code / low-code orchestration layer",
          competitor: "Workflow builder, many integrations",
          hasBeanbag: true,
          hasCompetitor: true,
        },
        {
          feature: "Security",
          beanbag: "SOC 2, GDPR",
          competitor:
            "SOC2, HIPAA, GDPR compliance in higher & enterprise plans",
          hasBeanbag: true,
          hasCompetitor: true,
        },
      ];
    }
    if (competitorKey === "retell") {
      return [
        {
          feature: "LLM Model & Intelligence",
          beanbag:
            "Powered with native speech-to-speech reasoning, doesn't need separate STT/TTS layers",
          competitor:
            "Offers choice of models (e.g., GPT-5 family, Claude, etc.)",
          hasBeanbag: true,
          hasCompetitor: true,
        },
        {
          feature: "Custom LLM Support",
          beanbag: "Flexible - bring your own model or use the default option",
          competitor: "Supports custom LLMs; option to use different LLM tiers",
          hasBeanbag: true,
          hasCompetitor: true,
        },
        {
          feature: "Voice & Audio Quality",
          beanbag: "Natural, expressive, and human-like voices",
          competitor:
            "Multiple voice engines: ElevenLabs, Cartesia, OpenAI voices",
          hasBeanbag: true,
          hasCompetitor: true,
        },
        {
          feature: "Latency",
          beanbag: "Sub-300ms end-to-end latency",
          competitor: "~800 ms latency in many cases",
          hasBeanbag: true,
          hasCompetitor: false,
        },
        {
          feature: "Interruptibility (Barge-In Handling)",
          beanbag:
            "Smooth mid-sentence interruption and recovery, built-in VAD events",
          competitor: "Supports interrupt detection and end-of-turn events",
          hasBeanbag: true,
          hasCompetitor: true,
        },
        {
          feature: "Multilingual Support",
          beanbag: "Supports 50+ languages",
          competitor: "Supports 30+ languages",
          hasBeanbag: true,
          hasCompetitor: false,
        },
        {
          feature: "Custom Telephony Integration",
          beanbag: "Native telephony + custom telephony integration",
          competitor: "Supports Twilio, Vonage, Telynx",
          hasBeanbag: true,
          hasCompetitor: true,
        },
        {
          feature: "Warm Transfer",
          beanbag: "Easy hand-off to a live human agent",
          competitor: "Built-in warm transfer support",
          hasBeanbag: true,
          hasCompetitor: true,
        },
        {
          feature: "IVR & DTMF Support",
          beanbag:
            "Touch-tone recognition and menu navigation supported for compatibility",
          competitor: "Supported",
          hasBeanbag: true,
          hasCompetitor: true,
        },
        {
          feature: "Post-Call Analytics",
          beanbag: "Transcripts, summaries, and call analytics",
          competitor:
            "Transcripts, analytics, monitoring, simulation, & testing tools",
          hasBeanbag: true,
          hasCompetitor: true,
        },
        {
          feature: "Ease of Use",
          beanbag: "No-code / low-code orchestration layer",
          competitor:
            "Low-code / visual builder; drag-and-drop; some technical setup for advanced customization",
          hasBeanbag: true,
          hasCompetitor: true,
        },
        {
          feature: "Security",
          beanbag: "SOC 2, GDPR",
          competitor: "Varies by provider; data flows across multiple vendors",
          hasBeanbag: true,
          hasCompetitor: true,
        },
      ];
    }

    // Default Vapi features
    return [
      {
        feature: "LLM Model & Intelligence",
        beanbag:
          "Powered with native speech-to-speech reasoning, doesn't need separate STT/TTS layers",
        competitor:
          "Relies on external STT + LLM + TTS pipeline with higher latency",
        hasBeanbag: true,
        hasCompetitor: false,
      },
      {
        feature: "Custom LLM Support",
        beanbag: "Flexible - bring your own model or use the default option",
        competitor: "Supports plugging in any LLM",
        hasBeanbag: true,
        hasCompetitor: true,
      },
      {
        feature: "Voice & Audio Quality",
        beanbag: "Natural, expressive, and human-like voices",
        competitor: "Quality depends on the chosen third-party TTS",
        hasBeanbag: true,
        hasCompetitor: false,
      },
      {
        feature: "Latency",
        beanbag: "Sub-300ms end-to-end latency",
        competitor: "500 ms latency, varies with chosen providers",
        hasBeanbag: true,
        hasCompetitor: false,
      },
      {
        feature: "Interruptibility (Barge-In Handling)",
        beanbag:
          "Smooth mid-sentence interruption and recovery, built-in VAD events",
        competitor: "Supported, but requires more configuration",
        hasBeanbag: true,
        hasCompetitor: true,
      },
      {
        feature: "Multilingual Support",
        beanbag: "Supports 50+ languages",
        competitor: "Supports 100+ languages via external providers",
        hasBeanbag: true,
        hasCompetitor: true,
      },
      {
        feature: "Custom Telephony Integration",
        beanbag: "Native telephony + custom telephony integration",
        competitor: "Native telephony + custom integration",
        hasBeanbag: true,
        hasCompetitor: true,
      },
      {
        feature: "Warm Transfer",
        beanbag: "Easy hand-off to a live human agent",
        competitor: "Built-in warm transfer",
        hasBeanbag: true,
        hasCompetitor: true,
      },
      {
        feature: "IVR & DTMF Support",
        beanbag: "Touch-tone recognition and menu navigation supported",
        competitor: "Supported",
        hasBeanbag: true,
        hasCompetitor: true,
      },
      {
        feature: "Post-Call Analytics",
        beanbag: "Transcripts, summaries, and call analytics",
        competitor: "Basic transcripts; advanced analytics via integrations",
        hasBeanbag: true,
        hasCompetitor: true,
      },
      {
        feature: "Ease of Use",
        beanbag: "No-code / low-code orchestration layer",
        competitor:
          "Visual builder + SDKs; heavier dev work for advanced flows",
        hasBeanbag: true,
        hasCompetitor: false,
      },
      {
        feature: "Security",
        beanbag: "SOC 2, GDPR",
        competitor: "Varies by provider; data flows across multiple vendors",
        hasBeanbag: true,
        hasCompetitor: true,
      },
      {
        feature: "Real-time Monitoring",
        beanbag: "Live call monitoring and intervention capabilities",
        competitor: "Post-call monitoring only",
        hasBeanbag: true,
        hasCompetitor: false,
      },
      {
        feature: "API Documentation",
        beanbag: "Comprehensive docs with code examples and SDKs",
        competitor: "Well-documented REST APIs",
        hasBeanbag: true,
        hasCompetitor: true,
      },
      {
        feature: "Pricing Model",
        beanbag: "Transparent per-minute pricing with no hidden fees",
        competitor: "Complex tiered pricing with setup fees",
        hasBeanbag: true,
        hasCompetitor: false,
      },
    ];
  };

  const featuresData = getFeaturesData(competitorKey);

  const getFaqData = (competitorKey) => {
    if (competitorKey === "openmic") {
      return [
        {
          question: "How is Beanbag AI different from OpenMic?",
          answer:
            "Beanbag AI is an end-to-end voice AI platform. It handles speech in real time without depending on separate STT or TTS layers, which makes conversations faster, smoother, and more natural compared to traditional voice stacks.",
        },
        {
          question: "How does Beanbag AI work?",
          answer:
            "Users can create AI agents using pre-built modules and a no-code/low-code builder. It integrates with various data sources and tools to automate processes.",
        },
        {
          question: "What languages does Beanbag AI support?",
          answer:
            "Beanbag AI supports 50+ languages and can switch mid-conversation, allowing you to serve customers globally with the same natural experience.",
        },
        {
          question: "Can customers interrupt the AI agent while it's speaking?",
          answer:
            "Yes. Beanbag AI agents handle interruptions smoothly. If a customer speaks mid-response, the agent pauses, listens, and adjusts in real time.",
        },
        {
          question: "Can I integrate Beanbag AI with my existing phone system?",
          answer:
            "Yes, Beanbag AI connects seamlessly with SIP and Twilio, so you can use it with your current phone numbers, routing, or call flows.",
        },
        {
          question: "Does Beanbag AI support IVR and DTMF menus?",
          answer:
            'Yes, Beanbag AI supports IVR and touch-tone navigation for enterprises that still need them. But our agents are designed to hold natural conversations, so you don\'t have to rely on "Press 1 for Sales" menus unless required.',
        },
        {
          question: "Does Beanbag AI provide analytics and call summaries?",
          answer:
            "Yes, every conversation can be transcribed, summarized, and analyzed. You get dashboards for real-time monitoring as well as post-call insights like FAQs, customer sentiment, and action items.",
        },
        {
          question: "How does Beanbag AI keep conversations secure?",
          answer:
            "Beanbag AI emphasizes data security with features like anonymization, encryption, and compliance with standards such as GDPR and SOC 2 for voice data. It requires clear user consent for data collection, especially in voice surveys, to build trust and avoid adoption barriers. Customer data is never shared or used for training outside your account.",
        },
        {
          question: "What's the pricing model for Beanbag AI?",
          answer:
            "Beanbag AI offers simple, transparent per-minute pricing. Unlike OpenMic, which adds separate charges for STT, TTS, and AI models, all costs are included upfront.",
        },
        {
          question: "Can I bring my own AI model or tools?",
          answer:
            "Yes, Beanbag AI is flexible. You can run on our default voice AI system or connect your own models and tools if your enterprise setup requires it.",
        },
        {
          question: "How quickly can I launch a voice agent with Beanbag AI?",
          answer:
            "You can go live in under a week. With pre-built templates and a workflow builder, setup is fast and doesn't need heavy engineering work.",
        },
        {
          question: "Can Beanbag AI handle large call volumes?",
          answer:
            "Yes, Beanbag AI scales to thousands of concurrent calls while maintaining reliability and low latency, making it fit for both startups and enterprises.",
        },
        {
          question: "Can calls be transferred to a live agent?",
          answer:
            "Yes, Beanbag AI supports warm transfers, so your customers can be routed to a human agent whenever needed",
        },
        {
          question: "Does Beanbag AI offer a free trial or demo?",
          answer:
            "Yes, Beanbag AI offers a free demo. Fill out the contact form to schedule a demo.",
        },
      ];
    }
    if (competitorKey === "retell") {
      return [
        {
          question: "How is Beanbag AI different from Retell AI?",
          answer:
            "Beanbag AI is an end-to-end voice AI platform. It handles speech in real time without depending on separate STT or TTS layers, which makes conversations faster, smoother, and more natural compared to traditional voice stacks.",
        },
        {
          question: "How does Beanbag AI work?",
          answer:
            "Users can create AI agents using pre-built modules and a no-code/low-code builder. It integrates with various data sources and tools to automate processes.",
        },
        {
          question: "What languages does Beanbag AI support?",
          answer:
            "Beanbag AI supports 50+ languages and can switch mid-conversation, allowing you to serve customers globally with the same natural experience.",
        },
        {
          question: "Can customers interrupt the AI agent while it's speaking?",
          answer:
            "Yes. Beanbag AI agents handle interruptions smoothly. If a customer speaks mid-response, the agent pauses, listens, and adjusts in real time.",
        },
        {
          question: "Can I integrate Beanbag AI with my existing phone system?",
          answer:
            "Yes, Beanbag AI connects seamlessly with SIP and Twilio, so you can use it with your current phone numbers, routing, or call flows.",
        },
        {
          question: "Does Beanbag AI support IVR and DTMF menus?",
          answer:
            'Yes, Beanbag AI supports IVR and touch-tone navigation for enterprises that still need them. But our agents are designed to hold natural conversations, so you don\'t have to rely on "Press 1 for Sales" menus unless required.',
        },
        {
          question: "Does Beanbag AI provide analytics and call summaries?",
          answer:
            "Yes, every conversation can be transcribed, summarized, and analyzed. You get dashboards for real-time monitoring as well as post-call insights like FAQs, customer sentiment, and action items.",
        },
        {
          question: "How does Beanbag AI keep conversations secure?",
          answer:
            "Beanbag AI emphasizes data security with features like anonymization, encryption, and compliance with standards such as GDPR and SOC 2 for voice data. It requires clear user consent for data collection, especially in voice surveys, to build trust and avoid adoption barriers. Customer data is never shared or used for training outside your account.",
        },
        {
          question: "What's the pricing model for Beanbag AI?",
          answer:
            "Beanbag AI offers simple, transparent per-minute pricing. Unlike Retell AI, which adds separate charges for STT, TTS, and AI models, all costs are included upfront.",
        },
        {
          question: "Can I bring my own AI model or tools?",
          answer:
            "Yes, Beanbag AI is flexible. You can run on our default voice AI system or connect your own models and tools if your enterprise setup requires it.",
        },
        {
          question: "How quickly can I launch a voice agent with Beanbag AI?",
          answer:
            "You can go live in under a week. With pre-built templates and a workflow builder, setup is fast and doesn't need heavy engineering work.",
        },
        {
          question: "Can Beanbag AI handle large call volumes?",
          answer:
            "Yes, Beanbag AI scales to thousands of concurrent calls while maintaining reliability and low latency, making it fit for both startups and enterprises.",
        },
        {
          question: "Can calls be transferred to a live agent?",
          answer:
            "Yes, Beanbag AI supports warm transfers, so your customers can be routed to a human agent whenever needed",
        },
        {
          question: "Does Beanbag AI offer a free trial or demo?",
          answer:
            "Yes, Beanbag AI offers a free demo. Fill out the contact form to schedule a demo.",
        },
      ];
    }

    // Default Vapi FAQ
    return [
      {
        question: "How is Beanbag AI different from Vapi?",
        answer:
          "Beanbag AI is an end-to-end voice AI platform. It handles speech in real time without depending on separate STT or TTS layers, which makes conversations faster, smoother, and more natural compared to traditional voice stacks.",
      },
      {
        question: "How does Beanbag AI work?",
        answer:
          "Users can create AI agents using pre-built modules and a no-code/low-code builder. It integrates with various data sources and tools to automate processes.",
      },
      {
        question: "What languages does Beanbag AI support?",
        answer:
          "Beanbag AI supports 50+ languages and can switch mid-conversation, allowing you to serve customers globally with the same natural experience.",
      },
      {
        question: "Can customers interrupt the AI agent while it's speaking?",
        answer:
          "Yes. Beanbag AI agents handle interruptions smoothly. If a customer speaks mid-response, the agent pauses, listens, and adjusts in real time.",
      },
      {
        question: "Can I integrate Beanbag AI with my existing phone system?",
        answer:
          "Yes, Beanbag AI connects seamlessly with SIP and Twilio, so you can use it with your current phone numbers, routing, or call flows.",
      },
      {
        question: "Does Beanbag AI support IVR and DTMF menus?",
        answer:
          'Yes, Beanbag AI supports IVR and touch-tone navigation for enterprises that still need them. But our agents are designed to hold natural conversations, so you don\'t have to rely on "Press 1 for Sales" menus unless required.',
      },
      {
        question: "Does Beanbag AI provide analytics and call summaries?",
        answer:
          "Yes, every conversation can be transcribed, summarized, and analyzed. You get dashboards for real-time monitoring as well as post-call insights like FAQs, customer sentiment, and action items.",
      },
      {
        question: "How does Beanbag AI keep conversations secure?",
        answer:
          "Beanbag AI emphasizes data security with features like anonymization, encryption, and compliance with standards such as GDPR and SOC 2 for voice data. It requires clear user consent for data collection, especially in voice surveys, to build trust and avoid adoption barriers. Customer data is never shared or used for training outside your account.",
      },
      {
        question: "What's the pricing model for Beanbag AI?",
        answer:
          "Beanbag AI offers simple, transparent per-minute pricing. Unlike Vapi, which adds separate charges for STT, TTS, and AI models, all costs are included upfront.",
      },
      {
        question: "Can I bring my own AI model or tools?",
        answer:
          "Yes, Beanbag AI is flexible. You can run on our default voice AI system or connect your own models and tools if your enterprise setup requires it.",
      },
      {
        question: "How quickly can I launch a voice agent with Beanbag AI?",
        answer:
          "You can go live in under a week. With pre-built templates and a workflow builder, setup is fast and doesn't need heavy engineering work.",
      },
      {
        question: "Can Beanbag AI handle large call volumes?",
        answer:
          "Yes, Beanbag AI scales to thousands of concurrent calls while maintaining reliability and low latency, making it fit for both startups and enterprises.",
      },
      {
        question: "Can calls be transferred to a live agent?",
        answer:
          "Yes, Beanbag AI supports warm transfers, so your customers can be routed to a human agent whenever needed",
      },
      {
        question: "Does Beanbag AI offer a free trial or demo?",
        answer:
          "Yes, Beanbag AI offers a free demo. Fill out the contact form to schedule a demo.",
      },
    ];
  };

  const faqData = getFaqData(competitorKey);

  const styles = {
    body: {
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      lineHeight: "1.6",
      color: "#333",
      background: "#ffffff",
      margin: 0,
      padding: 0,
    },
    container: {
      maxWidth: "1200px",
      margin: "0 auto",
      padding: "0 20px",
    },
    header: {
      position: "fixed",
      top: 0,
      width: "100%",
      background: "rgba(255, 255, 255, 0.98)",
      backdropFilter: "blur(10px)",
      zIndex: 1000,
      transition: "all 0.3s ease",
      boxShadow: "0 2px 20px rgba(0, 0, 0, 0.1)",
    },
    nav: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    hero: {
      padding: "120px 0 80px",
      background:
        "radial-gradient(ellipse at top, rgba(226, 107, 74, 0.05) 0%, transparent 70%)",
      textAlign: "center",
    },
    heroTitle: {
      fontSize: "clamp(2rem, 5vw, 4rem)",
      fontWeight: "700",
      marginBottom: "24px",
      background: "linear-gradient(135deg, #333,rgb(226, 104, 74))",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
      animation: "fadeInUp 1s ease-out",
      lineHeight: "1.4",
    },
    heroText: {
      fontSize: "clamp(1rem, 2.5vw, 1.25rem)",
      maxWidth: "800px",
      margin: "0 auto 40px",
      color: "#666",
      animation: "fadeInUp 1s ease-out 0.2s both",
    },
  };

  const calculatePricingVisualization = (beanbagPrice, competitorPrice) => {
    // Extract numeric values from pricing strings
    const beanbagNum = parseFloat(
      beanbagPrice.replace("$", "").replace("/min", "")
    );
    const competitorNum = parseFloat(
      competitorPrice.replace("$", "").replace("/min", "")
    );

    // Calculate the percentage that Beanbag represents of the competitor's price
    const beanbagPercentage = (beanbagNum / competitorNum) * 100;
    const savingsPercentage = 100 - beanbagPercentage;

    // Ensure minimum widths for visual clarity
    const minBeanbagWidth = 35; // Minimum 35% for readability
    const minSavingsWidth = 30; // Minimum 20% for "savings" text

    let adjustedBeanbagPercentage = Math.max(
      beanbagPercentage,
      minBeanbagWidth
    );
    let adjustedSavingsPercentage = Math.max(
      savingsPercentage,
      minSavingsWidth
    );

    // If both minimums would exceed 100%, proportionally adjust
    if (adjustedBeanbagPercentage + adjustedSavingsPercentage > 100) {
      const ratio =
        100 / (adjustedBeanbagPercentage + adjustedSavingsPercentage);
      adjustedBeanbagPercentage *= ratio;
      adjustedSavingsPercentage *= ratio;
    }

    return {
      beanbagPercentage: Math.round(beanbagPercentage),
      savingsPercentage: Math.round(savingsPercentage),
      adjustedBeanbagPercentage: Math.round(adjustedBeanbagPercentage),
      adjustedSavingsPercentage: Math.round(adjustedSavingsPercentage),
      beanbagNum,
      competitorNum,
    };
  };

  return (
    <div style={styles.body}>
      <style>
        {`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
              box-shadow: 0 4px 20px rgba(226, 107, 74, 0.3);
            }
            50% {
              transform: scale(1.05);
              box-shadow: 0 8px 40px rgba(226, 107, 74, 0.5);
            }
          }

          @keyframes ping {
            0% {
              transform: translate(-50%, -50%) scale(1);
              opacity: 1;
            }
            75%, 100% {
              transform: translate(-50%, -50%) scale(2);
              opacity: 0;
            }
          }

          /* Enhanced responsive styles */
          @media (max-width: 900px) {
            .section-title {
              font-size: 2rem !important;
            }
            .hero-title {
              font-size: 2.5rem !important;
            }
            .hero-text {
              font-size: 1.1rem !important;
            }
            .hero-container {
              flex-direction: column !important;
              align-items: center;
              gap: 2rem !important;
            }
            .hero-content {
              max-width: 100% !important;
              text-align: center;
            }
          }

          @media (max-width: 768px) {
            .section-title {
              font-size: 1.8rem !important;
            }
            .hero-title {
              font-size: 2.2rem !important;
            }
            .hero-text {
              font-size: 1rem !important;
            }
            .pricing-container {
              flex-direction: column !important;
              gap: 2rem !important;
            }
            .pricing-bar-wrapper {
              margin-left: 0 !important;
              margin-right: 0 !important;
            }
          }

          @media (max-width: 600px) {
            .section-title {
              font-size: 1.6rem !important;
            }
            .hero-title {
              font-size: 2rem !important;
            }
            .hero-text {
              font-size: 0.95rem !important;
            }
            .table-wrapper {
              overflow-x: auto !important;
              -webkit-overflow-scrolling: touch !important;
            }
            .table-cell-mobile {
              min-width: 120px !important;
              font-size: 0.85rem !important;
            }
            .icon-wrapper {
              width: 60px !important;
              height: 60px !important;
            }
            .icon-wrapper img {
              width: 40px !important;
              height: 40px !important;
            }
          }

          @media (max-width: 480px) {
            .hero-title {
              font-size: 1.8rem !important;
            }
            .hero-text {
              font-size: 0.9rem !important;
            }
            .section-title {
              font-size: 1.4rem !important;
            }
            .card-content {
              padding: 1.5rem !important;
            }
            .accordion-title {
              font-size: 0.95rem !important;
            }
          }
        `}
      </style>

      <header style={styles.header}>
        <div style={styles.container}>
          <nav style={styles.nav}>
            <Navbar />
          </nav>
        </div>
      </header>

      <main>
        <section style={styles.hero}>
          <div
            style={{
              ...styles.container,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "2rem",
            }}
            className="hero-container"
          >
            <div
              style={{ flex: { xs: "unset", lg: 1 }, maxWidth: "800px" }}
              className="hero-content"
            >
              <h1 style={styles.heroTitle} className="hero-title">
                Beanbag AI vs {competitor.name}
              </h1>
              <p style={styles.heroText} className="hero-text">
                When it comes to Beanbag AI vs {competitor.name}, Beanbag AI,
                with its unified speech-to-speech model, is built for
                reliability, speed, and high-quality real-time conversations at
                scale without compromising performance, which {competitor.name}{" "}
                cannot match.
              </p>
              <Button
                variant="text"
                onClick={() =>
                  window.open("https://www.beanbag.ai/voice", "_blank")
                }
                sx={{
                  textDecoration: "underline",
                  textTransform: "none",
                  fontSize: { xs: "14px", sm: "16px", md: "18px", lg: "20px" },
                  fontWeight: 500,
                  p: { xs: "6px 12px", sm: "8px 16px" },
                  borderRadius: "8px",
                  position: "relative",
                  transition: "all 0.3s ease",
                  backgroundColor: "rgba(234, 99, 71, 0.1)",
                  color: "#ea6347",
                  textDecoration: "none",
                  "&:hover": {
                    transform: "translateY(-2px)",
                  },
                  "&:active": {
                    transform: "translateY(0px)",
                  },
                }}
              >
                Try Beanbag AI
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* Overview Section */}
      <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor: "#fafafa" }}>
        <Container
          style={{ maxWidth: { xs: "100%", sm: "90%", lg: "80%", xl: "80%" } }}
          sx={{ px: { xs: 2, sm: 3, md: 4 } }}
        >
          <Typography
            variant="h2"
            className="section-title"
            sx={{
              fontSize: { xs: "1.8rem", sm: "2.2rem", md: "2.5rem" },
              fontWeight: 700,
              mb: { xs: 4, md: 6 },
              textAlign: "center",
            }}
          >
            Beanbag AI vs {competitor.name}
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: { xs: 3, md: 4, lg: 6 },
              flexDirection: { xs: "column", lg: "row" },
            }}
          >
            {/* Beanbag AI Box */}
            <Card
              sx={{
                width: { xs: "100%", lg: "45%" },
                maxWidth: { xs: "100%", lg: "800px" },
                borderRadius: 4,
                borderLeft: "4px solid #ea6347",
                "&:hover": {
                  transform: { xs: "translateY(-4px)", md: "translateY(-8px)" },
                  boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
                },
                transition: "all 0.3s ease",
              }}
            >
              <CardContent
                className="card-content"
                sx={{
                  p: { xs: 3, md: 4 },
                  textAlign: "center",
                }}
              >
                <img
                  style={{
                    width: "50px",
                    height: "50px",
                    objectFit: "contain",
                  }}
                  src={beanbagLogo}
                  alt="beanbag"
                />
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    fontSize: { xs: "1.2rem", md: "1.5rem" },
                  }}
                >
                  Beanbag AI
                </Typography>

                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    lineHeight: 1.7,
                    textAlign: "left",
                    fontSize: { xs: "0.9rem", md: "1rem" },
                  }}
                >
                  Beanbag AI is a low-code, no-code DIY platform that helps
                  companies build and customize their own AI voice agents. This
                  lets them define the conversation flow, choose the tone of
                  voice, set escalation rules, and connect workflows to specific
                  business needs.
                </Typography>
              </CardContent>
            </Card>

            {/* VS Circle */}
            <Box
              sx={{
                width: { xs: 60, md: 80 },
                height: { xs: 60, md: 80 },
                background: "linear-gradient(135deg, #ea6347, #ff5722)",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontWeight: "bold",
                fontSize: { xs: "1rem", md: "1.2rem" },
                flexShrink: 0,
                boxShadow: "0 4px 20px rgba(234, 99, 71, 0.3)",
              }}
            >
              VS
            </Box>

            {/* Competitor Box */}
            <Card
              sx={{
                width: { xs: "100%", lg: "45%" },
                maxWidth: { xs: "100%", lg: "800px" },
                borderRadius: 4,
                borderLeft: "4px solid #666",
                "&:hover": {
                  transform: { xs: "translateY(-4px)", md: "translateY(-8px)" },
                  boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
                },
                transition: "all 0.3s ease",
              }}
            >
              <CardContent
                className="card-content"
                sx={{
                  p: { xs: 3, md: 4 },
                  textAlign: "center",
                }}
              >
                <img
                  style={{
                    width: "50px",
                    height: "50px",
                    objectFit: "contain",
                  }}
                  src={competitor.logo}
                  alt={competitor.name}
                />

                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    fontSize: { xs: "1.2rem", md: "1.5rem" },
                  }}
                >
                  {competitor.name}
                </Typography>

                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    lineHeight: 1.7,
                    textAlign: "left",
                    fontSize: { xs: "0.9rem", md: "1rem" },
                  }}
                >
                  {competitor.description}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Container>
      </Box>

      {/* Features Comparison Table */}
      <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor: "white" }}>
        <Container
          style={{ maxWidth: { xs: "100%", sm: "90%", lg: "80%", xl: "80%" } }}
          sx={{ px: { xs: 2, sm: 3, md: 4 } }}
        >
          <Typography
            variant="h2"
            className="section-title"
            sx={{
              fontSize: { xs: "1.8rem", sm: "2.2rem", md: "2.5rem" },
              fontWeight: 700,
              mb: { xs: 4, md: 6 },
              textAlign: "center",
            }}
          >
            Features Comparison
          </Typography>

          <Box className="table-wrapper" sx={{ overflowX: "auto" }}>
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                overflow: "hidden",
                minWidth: { xs: "800px", md: "auto" },
              }}
            >
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      background:
                        "linear-gradient(135deg, #333,rgb(226, 104, 74))",
                    }}
                  >
                    <TableCell
                      sx={{
                        color: "white",
                        fontWeight: 600,
                        fontSize: { xs: "0.9rem", md: "1.1rem" },
                        py: { xs: 2, md: 3 },
                        width: { xs: "300px", md: "500px" },
                      }}
                    ></TableCell>
                    <TableCell
                      sx={{
                        color: "white",
                        fontWeight: 600,
                        fontSize: { xs: "0.9rem", md: "1.1rem" },
                        py: { xs: 2, md: 3 },
                        textAlign: "center",
                        position: "relative",
                        width: { xs: "250px", md: "40%" },
                      }}
                      className="table-cell-mobile"
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: { xs: 1, md: 2 },
                        }}
                      >
                        {/* Logo positioned above the text */}
                        <Box
                          className="icon-wrapper"
                          sx={{
                            width: { xs: 60, md: 80 },
                            height: { xs: 60, md: 80 },
                            borderRadius: 2,
                            backgroundColor: "rgba(255, 255, 255, 0.15)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backdropFilter: "blur(10px)",
                            border: "1px solid rgba(255, 255, 255, 0.2)",
                          }}
                        >
                          <img
                            style={{
                              width: "40px",
                              height: "40px",
                              objectFit: "contain",
                            }}
                            src={beanbagLogo}
                            alt="Beanbag AI"
                          />
                        </Box>
                        <Typography
                          variant="body1"
                          sx={{
                            color: "white",
                            fontWeight: 600,
                            fontSize: { xs: "0.9rem", md: "1.1rem" },
                          }}
                        >
                          Beanbag AI
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "white",
                        fontWeight: 600,
                        fontSize: { xs: "0.9rem", md: "1.1rem" },
                        py: { xs: 2, md: 3 },
                        textAlign: "center",
                        position: "relative",
                        width: { xs: "250px", md: "40%" },
                      }}
                      className="table-cell-mobile"
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: { xs: 1, md: 2 },
                        }}
                      >
                        {/* Logo positioned above the text */}
                        <Box
                          className="icon-wrapper"
                          sx={{
                            width: { xs: 60, md: 80 },
                            height: { xs: 60, md: 80 },
                            borderRadius: 2,
                            backgroundColor: "rgba(255, 255, 255, 0.15)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backdropFilter: "blur(10px)",
                            border: "1px solid rgba(255, 255, 255, 0.2)",
                          }}
                        >
                          <img
                            style={{
                              width: "40px",
                              height: "40px",
                              objectFit: "contain",
                            }}
                            src={competitor.logo}
                            alt={competitor.name}
                          />
                        </Box>
                        <Typography
                          variant="body1"
                          sx={{
                            color: "white",
                            fontWeight: 600,
                            fontSize: { xs: "0.9rem", md: "1.1rem" },
                          }}
                        >
                          {competitor.name}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {featuresData.map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor: index % 2 === 0 ? "white" : "#f9f9f9",
                        "&:hover": { backgroundColor: "#f1f1f1" },
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          py: { xs: 2, md: 3 },
                          fontSize: { xs: "0.85rem", md: "1rem" },
                        }}
                      >
                        {row.feature}
                      </TableCell>
                      <TableCell sx={{ py: { xs: 2, md: 3 } }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: { xs: 1, md: 1.5 },
                          }}
                        >
                          <CheckCircle
                            sx={{
                              color: "#4caf50",
                              mt: 0.2,
                              flexShrink: 0,
                              fontSize: { xs: "1rem", md: "1.2rem" },
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: { xs: "0.8rem", md: "0.95rem" },
                              lineHeight: 1.4,
                            }}
                          >
                            {row.beanbag}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: { xs: 2, md: 3 } }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: { xs: 1, md: 1.5 },
                          }}
                        >
                          {row.hasCompetitor ? (
                            <>
                              <CheckCircle
                                sx={{
                                  color: "#4caf50",
                                  mt: 0.2,
                                  flexShrink: 0,
                                  fontSize: { xs: "1rem", md: "1.2rem" },
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#666",
                                  fontSize: { xs: "0.8rem", md: "0.95rem" },
                                  lineHeight: 1.4,
                                }}
                              >
                                {row.competitor}
                              </Typography>
                            </>
                          ) : (
                            <>
                              <Cancel
                                sx={{
                                  color: "#f44336",
                                  mt: 0.2,
                                  flexShrink: 0,
                                  fontSize: { xs: "1rem", md: "1.2rem" },
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#666",
                                  fontSize: { xs: "0.8rem", md: "0.95rem" },
                                  lineHeight: 1.4,
                                }}
                              >
                                {row.competitor}
                              </Typography>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor: "#fafafa" }}>
        <Container
          style={{ maxWidth: { xs: "100%", sm: "90%", lg: "80%", xl: "80%" } }}
          sx={{ px: { xs: 2, sm: 3, md: 4 } }}
        >
          <Typography
            variant="h2"
            className="section-title"
            sx={{
              fontSize: { xs: "1.8rem", sm: "2.2rem", md: "2.5rem" },
              fontWeight: 700,
              mb: { xs: 4, md: 6 },
              textAlign: "center",
            }}
          >
            Pricing
          </Typography>

          {(() => {
            const visualization = calculatePricingVisualization(
              "$0.27",
              competitor.pricing
            );

            return (
              <Box
                sx={{
                  maxWidth: 1200,
                  mx: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: { xs: 3, md: 4 },
                }}
              >
                {/* Beanbag AI Pricing */}
                <Card
                  sx={{
                    p: { xs: 3, md: 4 },
                    borderRadius: 3,
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                    backgroundColor: "white",
                    border: "2px solid #ea6347",
                  }}
                >
                  <Box
                    className="pricing-container"
                    sx={{
                      display: "flex",
                      alignItems: { xs: "flex-start", md: "center" },
                      justifyContent: "space-between",
                      flexDirection: { xs: "column", md: "row" },
                      gap: { xs: 2, md: 0 },
                    }}
                  >
                    {/* Left - Company Info */}
                    <Box
                      sx={{ flexShrink: 0, width: { xs: "100%", md: "200px" } }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          mb: 1,
                          color: "#1a1a1a",
                          fontSize: { xs: "1.5rem", md: "2rem" },
                        }}
                      >
                        Beanbag AI
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          color: "#ea6347",
                          fontWeight: 600,
                          fontSize: { xs: "1.1rem", md: "1.25rem" },
                        }}
                      >
                        $0.27/min
                      </Typography>
                    </Box>

                    {/* Center - Proportional Bar */}
                    <Box
                      className="pricing-bar-wrapper"
                      sx={{
                        flex: 1,
                        marginLeft: { xs: 0, md: "15%" },
                        marginRight: { xs: 0, md: 5 },
                        width: { xs: "100%", md: "auto" },
                      }}
                    >
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        {/* Icons positioned only above the Beanbag portion */}
                        <Box sx={{ display: "flex", mb: 1 }}>
                          <Box
                            sx={{
                              width: `${visualization.adjustedBeanbagPercentage}%`,
                              display: "flex",
                              justifyContent: "center",
                              gap: { xs: 0.5, md: 1 },
                              flexWrap: "wrap",
                            }}
                          >
                            {[openAI, google, twilio, beanbagLogo].map(
                              (icon, i) => (
                                <Box
                                  key={i}
                                  sx={{
                                    width: { xs: 16, md: 20 },
                                    height: { xs: 16, md: 20 },
                                    backgroundColor: "#f5f5f5",
                                    borderRadius: "6px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <img
                                    src={icon}
                                    alt={`icon-${i}`}
                                    style={{
                                      width: "12px",
                                      height: "12px",
                                      objectFit: "contain",
                                    }}
                                  />
                                </Box>
                              )
                            )}
                          </Box>
                          <Box
                            sx={{
                              width: `${visualization.adjustedSavingsPercentage}%`,
                            }}
                          >
                            {/* Empty space above savings section */}
                          </Box>
                        </Box>

                        {/* Proportional Bar */}
                        <Box
                          sx={{
                            display: "flex",
                            height: { xs: "35px", md: "40px" },
                            borderRadius: { xs: "15px", md: "20px" },
                            overflow: "hidden",
                            border: "2px solid #ea6347",
                          }}
                        >
                          {/* Beanbag portion */}
                          <Box
                            sx={{
                              width: `${visualization.adjustedBeanbagPercentage}%`,
                              backgroundColor: "#ea6347",
                              color: "white",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: { xs: "0.8rem", md: "1rem" },
                              fontWeight: 600,
                              minWidth: "100px",
                            }}
                          >
                            $0.27/min
                          </Box>

                          {/* Savings portion */}
                          <Box
                            sx={{
                              width: `${visualization.adjustedSavingsPercentage}%`,
                              backgroundColor: "transparent",
                              border: "2px dotted #ea6347",
                              borderLeft: "none",
                              color: "#ea6347",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: { xs: "0.75rem", md: "0.9rem" },
                              fontWeight: 600,
                              minWidth: "80px",
                            }}
                          >
                            {visualization.savingsPercentage < 10
                              ? "More Savings"
                              : `${visualization.savingsPercentage}% Savings`}
                          </Box>
                        </Box>

                        {/* Labels positioned under their respective sections */}
                        <Box sx={{ display: "flex", mt: 1 }}>
                          <Box
                            sx={{
                              width: `${visualization.adjustedBeanbagPercentage}%`,
                              textAlign: "center",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#ea6347",
                                fontWeight: 600,
                                fontSize: { xs: "0.8rem", md: "0.9rem" },
                              }}
                            >
                              All Inclusive
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Card>

                {/* Competitor Pricing */}
                <Card
                  sx={{
                    p: { xs: 3, md: 4 },
                    borderRadius: 3,
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                    backgroundColor: "white",
                  }}
                >
                  <Box
                    className="pricing-container"
                    sx={{
                      display: "flex",
                      alignItems: { xs: "flex-start", md: "center" },
                      justifyContent: "space-between",
                      flexDirection: { xs: "column", md: "row" },
                      gap: { xs: 2, md: 0 },
                    }}
                  >
                    {/* Left - Company Info */}
                    <Box
                      sx={{ flexShrink: 0, width: { xs: "100%", md: "200px" } }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          mb: 1,
                          color: "#1a1a1a",
                          fontSize: { xs: "1.5rem", md: "2rem" },
                        }}
                      >
                        {competitor.name}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          color: "#666",
                          fontWeight: 500,
                          fontSize: { xs: "1.1rem", md: "1.25rem" },
                        }}
                      >
                        {competitor.pricing}
                      </Typography>
                    </Box>

                    {/* Center - Combined Bar + Labels + Icons */}
                    <Box
                      className="pricing-bar-wrapper"
                      sx={{
                        flex: 1,
                        marginLeft: { xs: 0, md: "15%" },
                        marginRight: { xs: 0, md: 5 },
                        width: { xs: "100%", md: "auto" },
                      }}
                    >
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        {/* Icons row above all segments */}
                        <Box sx={{ display: "flex", mb: 1 }}>
                          {competitor.pricingIcons.map((icons, i) => (
                            <Box
                              key={i}
                              sx={{
                                flex: 1,
                                display: "flex",
                                justifyContent: "center",
                                gap: { xs: 0.5, md: 1 },
                                flexWrap: "wrap",
                              }}
                            >
                              {icons.map((icon, j) => (
                                <Box
                                  key={j}
                                  sx={{
                                    width: { xs: 16, md: 20 },
                                    height: { xs: 16, md: 20 },
                                    backgroundColor: "#f5f5f5",
                                    borderRadius: "6px",
                                    border: "2px solid #fff",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <img
                                    src={icon}
                                    alt={`segment-${i}-icon-${j}`}
                                    style={{
                                      width: "12px",
                                      height: "12px",
                                      objectFit: "contain",
                                    }}
                                  />
                                </Box>
                              ))}
                            </Box>
                          ))}
                        </Box>

                        {/* Bar with breakdown */}
                        <Box
                          sx={{
                            display: "flex",
                            height: { xs: "35px", md: "40px" },
                            borderRadius: { xs: "15px", md: "20px" },
                            overflow: "hidden",
                          }}
                        >
                          {competitor.pricingBreakdown.map((item, i) => (
                            <Box
                              key={i}
                              sx={{
                                flex: 1,
                                backgroundColor: item.color,
                                color: i >= 3 ? "#2d3748" : "white",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: { xs: "0.7rem", md: "0.8rem" },
                                fontWeight: 600,
                              }}
                            >
                              {item.price}
                            </Box>
                          ))}
                        </Box>

                        {/* Labels under each segment */}
                        <Box sx={{ display: "flex", mt: 1 }}>
                          {competitor.pricingBreakdown.map((item, i) => (
                            <Box key={i} sx={{ flex: 1, textAlign: "center" }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: { xs: "0.6rem", md: "0.7rem" },
                                  fontWeight: 500,
                                  color: "#666",
                                }}
                              >
                                {item.label}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Card>
              </Box>
            );
          })()}

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: { xs: 4, md: 6 },
            }}
          >
            <Button
              variant="text"
              onClick={handleOpenModal}
              sx={{
                textDecoration: "underline",
                textTransform: "none",
                fontSize: { xs: "14px", sm: "16px", md: "18px", lg: "20px" },
                fontWeight: 500,
                p: { xs: "6px 12px", sm: "8px 16px" },
                borderRadius: "8px",
                position: "relative",
                transition: "all 0.3s ease",
                backgroundColor: "rgba(234, 99, 71, 0.1)",
                color: "#ea6347",
                textDecoration: "none",
                "&:hover": {
                  transform: "translateY(-2px)",
                },
                "&:active": {
                  transform: "translateY(0px)",
                },
              }}
            >
              Get a Custom Quote
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Call-to-Action Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          px: { xs: 1, sm: 2, md: 3 },
        }}
      >
        <Box
          sx={{
            py: { xs: 6, md: 10 },
            mx: { xs: 1, sm: 2, md: 4 },
            my: "50px",
            background: "linear-gradient(135deg, #333,rgb(226, 104, 74))",
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
            borderRadius: "15px",
            width: { xs: "95%", md: "95%" },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgb(249, 247, 245)",
              pointerEvents: "none",
            }}
          />

          <Container
            maxWidth="md"
            sx={{ position: "relative", zIndex: 1, px: { xs: 3, md: 4 } }}
          >
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "1.4rem", sm: "1.6rem", md: "2.2rem" },
                fontWeight: 700,
                mb: { xs: 3, md: 4 },
                lineHeight: 1.3,
              }}
            >
              From customer support automation to AI companions, power workflows
              that need speed and reliability.
            </Typography>

            <Typography
              variant="body1"
              sx={{
                mb: { xs: 4, md: 5 },
                fontSize: { xs: "1rem", md: "1.1rem" },
                lineHeight: 1.5,
              }}
            >
              Start building with Beanbag AI today.
            </Typography>

            <Button
              variant="text"
              onClick={handleOpenModal}
              sx={{
                textDecoration: "underline",
                textTransform: "none",
                fontSize: { xs: "14px", sm: "16px", md: "18px", lg: "20px" },
                fontWeight: 500,
                p: { xs: "6px 12px", sm: "8px 16px" },
                borderRadius: "8px",
                position: "relative",
                transition: "all 0.3s ease",
                backgroundColor: "rgba(234, 99, 71, 0.1)",
                color: "#ea6347",
                textDecoration: "none",
                "&:hover": {
                  transform: "translateY(-2px)",
                },
                "&:active": {
                  transform: "translateY(0px)",
                },
              }}
            >
              Contact Sales
            </Button>
          </Container>
        </Box>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor: "#fafafa" }}>
        <Container
          style={{ maxWidth: { xs: "100%", sm: "90%", lg: "80%", xl: "80%" } }}
          sx={{ px: { xs: 2, sm: 3, md: 4 } }}
        >
          <Typography
            variant="h2"
            className="section-title"
            sx={{
              fontSize: {
                xs: "1.8rem",
                sm: "2.2rem",
                md: "2.5rem",
                lg: "3rem",
              },
              fontWeight: 700,
              mb: { xs: 4, md: 6 },
              textAlign: "center",
            }}
          >
            Frequently Asked Questions
          </Typography>

          <Grid container columnSpacing={{ xs: 0, md: 2 }} rowSpacing={0}>
            {faqData.map((faq, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Accordion
                  expanded={expandedFAQ === `panel${index}`}
                  onChange={handleFAQChange(`panel${index}`)}
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    "&:before": { display: "none" },
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore sx={{ color: "#ea6347" }} />}
                    sx={{
                      py: 1,
                      "&:hover": { backgroundColor: "#fafafa" },
                    }}
                  >
                    <Typography
                      variant="h6"
                      className="accordion-title"
                      sx={{
                        fontWeight: 500,
                        fontSize: { xs: "0.95rem", sm: "1rem", md: "1.1rem" },
                        lineHeight: 1.4,
                      }}
                    >
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{
                        lineHeight: 1.6,
                        fontSize: { xs: "0.9rem", md: "1rem" },
                      }}
                    >
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Add the Calendar Modal at the end */}
      <CalendarModal open={modalOpen} handleClose={handleCloseModal} />
      <Footer />
    </div>
  );
};

export default ComparisonPage;
